import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = [
    "dropzone",
    "fileInput",
    "folderInput",
    "placeholder",
    "queue",
    "queueList",
    "summary",
    "successCount",
    "errorCount",
    "errorSummary",
    "uploadTemplate"
  ]

  static values = {
    url: String
  }

  connect() {
    this.uploads = []
    this.successCount = 0
    this.errorCount = 0
  }

  openFilePicker(event) {
    event.preventDefault()
    event.stopPropagation()
    this.fileInputTarget.click()
  }

  dragover(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.add("border-green-500", "bg-green-50")
  }

  dragleave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove("border-green-500", "bg-green-50")
  }

  drop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove("border-green-500", "bg-green-50")

    const files = event.dataTransfer.files
    if (files.length > 0) {
      this.processFiles(files)
    }
  }

  handleFiles(event) {
    const files = event.target.files
    if (files.length > 0) {
      this.processFiles(files)
    }
  }

  processFiles(files) {
    // Show queue, hide placeholder
    this.queueTarget.classList.remove("hidden")
    this.placeholderTarget.classList.add("hidden")
    this.summaryTarget.classList.add("hidden")

    // Reset counters
    this.successCount = 0
    this.errorCount = 0

    // Process each file
    Array.from(files).forEach(file => {
      this.uploadFile(file)
    })
  }

  uploadFile(file) {
    const uploadId = this.generateId()
    const uploadItem = this.createUploadItem(uploadId, file.name)

    this.queueListTarget.appendChild(uploadItem)

    // Get folder value
    const folder = this.hasFolderInputTarget ? this.folderInputTarget.value : ""

    // Create DirectUpload instance
    const upload = new DirectUpload(
      file,
      "/rails/active_storage/direct_uploads",
      this
    )

    // Start upload
    upload.create((error, blob) => {
      if (error) {
        this.uploadError(uploadId, error.message)
      } else {
        // Send blob to our controller to create Media record
        this.createMediaRecord(uploadId, blob.signed_id, file.name, folder)
      }
    })
  }

  createMediaRecord(uploadId, blobSignedId, filename, folder) {
    const formData = new FormData()
    formData.append("blob_signed_id", blobSignedId)
    formData.append("title", filename)
    if (folder) {
      formData.append("folder", folder)
    }

    fetch(this.urlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": this.csrfToken
      },
      body: formData
    })
    .then(response => response.json())
    .then(data => {
      if (data.error) {
        this.uploadError(uploadId, data.error)
      } else {
        this.uploadSuccess(uploadId, data)
      }
    })
    .catch(error => {
      this.uploadError(uploadId, error.message)
    })
  }

  uploadSuccess(uploadId, data) {
    const uploadItem = this.queueListTarget.querySelector(`[data-upload-id="${uploadId}"]`)
    if (!uploadItem) return

    const progressBar = uploadItem.querySelector("[data-progress]")
    const spinner = uploadItem.querySelector("[data-spinner]")
    const success = uploadItem.querySelector("[data-success]")
    const status = uploadItem.querySelector("[data-status]")

    progressBar.style.width = "100%"
    spinner.classList.add("hidden")
    success.classList.remove("hidden")
    status.textContent = "Complete"
    status.classList.remove("text-gray-500")
    status.classList.add("text-green-600")

    this.successCount++
    this.checkAllComplete()
  }

  uploadError(uploadId, errorMessage) {
    const uploadItem = this.queueListTarget.querySelector(`[data-upload-id="${uploadId}"]`)
    if (!uploadItem) return

    const spinner = uploadItem.querySelector("[data-spinner]")
    const error = uploadItem.querySelector("[data-error]")
    const status = uploadItem.querySelector("[data-status]")

    spinner.classList.add("hidden")
    error.classList.remove("hidden")
    status.textContent = errorMessage || "Upload failed"
    status.classList.remove("text-gray-500")
    status.classList.add("text-red-600")

    this.errorCount++
    this.checkAllComplete()
  }

  checkAllComplete() {
    const totalUploads = this.queueListTarget.children.length
    if ((this.successCount + this.errorCount) >= totalUploads) {
      this.showSummary()
    }
  }

  showSummary() {
    this.summaryTarget.classList.remove("hidden")
    this.successCountTarget.textContent = this.successCount

    if (this.errorCount > 0) {
      this.errorSummaryTarget.classList.remove("hidden")
      this.errorCountTarget.textContent = this.errorCount
    }
  }

  reset() {
    // Clear queue
    this.queueListTarget.innerHTML = ""
    this.queueTarget.classList.add("hidden")

    // Show placeholder
    this.placeholderTarget.classList.remove("hidden")

    // Hide summary
    this.summaryTarget.classList.add("hidden")

    // Reset file input
    this.fileInputTarget.value = ""

    // Reset counters
    this.successCount = 0
    this.errorCount = 0
  }

  createUploadItem(uploadId, filename) {
    const template = this.uploadTemplateTarget.content.cloneNode(true)
    const container = template.querySelector("div")

    container.setAttribute("data-upload-id", uploadId)
    container.querySelector("[data-filename]").textContent = filename

    return template
  }

  generateId() {
    return `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  // DirectUpload delegate methods
  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener("progress", event => this.directUploadDidProgress(event))
  }

  directUploadDidProgress(event) {
    const progress = (event.loaded / event.total) * 100
    // Update all progress bars (we could track individual uploads if needed)
    const progressBars = this.queueListTarget.querySelectorAll("[data-progress]")
    progressBars.forEach(bar => {
      if (bar.style.width === "0%" || bar.style.width === "") {
        bar.style.width = `${progress}%`
      }
    })
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
